import { cn } from "../../../lib/utils";
import {
  formatCount,
  formatCredits,
  formatMs,
  formatPercent,
  formatUsd,
  roundCredits,
  tokenCosts,
  type RunConfig,
  type RunDebug,
  type Trace,
  type TraceTarget,
} from "./trace";

const CELL = "px-2 py-0.5 text-right tabular-nums";
const HEAD = "px-2 py-0.5 text-right font-normal text-ds-slate-500";

function ShareBar({ share }: { share: number }): React.ReactElement {
  return (
    <span className="relative block h-2 w-full rounded-sm bg-ds-slate-100">
      <span
        className="absolute inset-y-0 left-0 rounded-sm bg-ds-blackberry-400"
        style={{ width: `${String(share * 100)}%` }}
      />
    </span>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <section className="flex flex-col gap-1">
      <h3 className="m-0 font-dm-sans text-sm font-semibold text-ds-black">
        {title}
      </h3>
      {children}
    </section>
  );
}

function TokenSplit({
  debug,
  config,
}: {
  debug: RunDebug;
  config: RunConfig | null;
}): React.ReactElement {
  if (config === null)
    return (
      <p className="m-0 text-ds-slate-500">
        The rates come with the run config, which exists once the run starts.
      </p>
    );
  const costs = tokenCosts(debug.rounds, config.rates);
  return (
    <>
      <dl className="m-0 grid grid-cols-2 gap-x-4 gap-y-0.5 @lg:grid-cols-4">
        {(
          [
            ["uncached input", costs.uncachedInput],
            ["cached input", costs.cachedInput],
            ["output, reasoning included", costs.output],
            ["saved by the cache", costs.cacheSavings],
          ] as const
        ).map(([label, usd]) => (
          <div key={label} className="flex flex-col">
            <dt className="text-ds-slate-500">{label}</dt>
            <dd className="m-0 tabular-nums">{formatUsd(usd)}</dd>
          </div>
        ))}
      </dl>
      <p className="m-0 text-ds-slate-500">
        {config.rates
          .map((rate) =>
            rate.usdPerMtok === null
              ? `${rate.model}: no rate`
              : `${rate.model}: $${String(rate.usdPerMtok.input)} in, ${rate.usdPerMtok.cachedInput === null ? "no cached rate" : `$${String(rate.usdPerMtok.cachedInput)} cached`}, $${String(rate.usdPerMtok.output)} out per million tokens`
          )
          .join(" · ")}
      </p>
      {costs.unpriced > 0 && (
        <p className="m-0 text-ds-clementine-600">
          {`${String(costs.unpriced)} rounds use a model without a rate and are left out of this split.`}
        </p>
      )}
      {Math.abs(
        (costs.uncachedInput + costs.cachedInput + costs.output) * 100 -
          costs.billed
      ) > 0.0001 && (
        <p className="m-0 text-ds-clementine-600">
          {`The priced rounds billed ${formatCredits(costs.billed)}: a Fast round served at the default tier, or a rate changed since the run.`}
        </p>
      )}
      {debug.run.inputTokens > 0 && debug.run.cachedInputTokens !== null && (
        <p className="m-0">
          {`Cache hit over the run: ${formatPercent(debug.run.cachedInputTokens / debug.run.inputTokens)}.`}
        </p>
      )}
    </>
  );
}

// Engine model calls only: web search, extraction and side calls are not here.
export function CostTab({
  debug,
  config,
  trace,
  onSelect,
}: {
  debug: RunDebug;
  config: RunConfig | null;
  trace: Trace;
  onSelect: (target: TraceTarget) => void;
}): React.ReactElement {
  const total = debug.run.credits;
  const byModel = [...new Set(debug.rounds.map((round) => round.modelId))].map(
    (model) => {
      const rounds = debug.rounds.filter((round) => round.modelId === model);
      const usage = rounds.flatMap((round) => {
        if (round.outcome === null) return [];
        switch (round.outcome.kind) {
          case "completed":
            return [round.outcome];
          case "failed":
            return [];
          default:
            throw new Error(round.outcome satisfies never);
        }
      });
      return {
        model,
        rounds: rounds.length,
        input: usage.reduce((sum, item) => sum + item.inputTokens, 0),
        cached: usage.reduce(
          (sum, item) =>
            sum +
            (item.cachedInputTokens === null ? 0 : item.cachedInputTokens),
          0
        ),
        output: usage.reduce((sum, item) => sum + item.outputTokens, 0),
        credits: rounds.reduce((sum, round) => sum + roundCredits(round), 0),
      };
    }
  );
  const top = trace.agents
    .flatMap((agent) => agent.rounds.map((round) => ({ agent, round })))
    .sort((left, right) => right.round.credits - left.round.credits)
    .slice(0, 10);
  const roundTotal = trace.agents.reduce(
    (sum, agent) => sum + agent.credits,
    0
  );
  return (
    <div className="flex flex-col gap-4 p-3 font-mono text-xs text-ds-black">
      <Section title="Total">
        <p className="m-0">
          {`${formatCredits(total)} for ${String(debug.rounds.length)} engine model calls. Web search, extraction and side calls are not counted.`}
        </p>
        {Math.abs(roundTotal - total) > 0.0001 && (
          <p className="m-0 text-ds-clementine-600">
            {`The rounds add up to ${formatCredits(roundTotal)}: runs older than the round rows, or rounds still open.`}
          </p>
        )}
      </Section>

      <Section title="By agent">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className={cn(HEAD, "text-left")}>agent</th>
              <th className={HEAD}>rounds</th>
              <th className={HEAD}>model time</th>
              <th className={HEAD}>cost</th>
              <th className={cn(HEAD, "w-1/4")}>share</th>
            </tr>
          </thead>
          <tbody>
            {trace.agents.map((agent) => (
              <tr key={agent.key}>
                <td className="max-w-0 truncate px-2 py-0.5">{agent.label}</td>
                <td className={CELL}>{agent.rounds.length}</td>
                <td className={CELL}>{formatMs(agent.modelMs)}</td>
                <td className={CELL}>{formatCredits(agent.credits)}</td>
                <td className="px-2 py-0.5">
                  <ShareBar share={total === 0 ? 0 : agent.credits / total} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      <Section title="By model">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className={cn(HEAD, "text-left")}>model</th>
              <th className={HEAD}>rounds</th>
              <th className={HEAD}>in</th>
              <th className={HEAD}>cached</th>
              <th className={HEAD}>out</th>
              <th className={HEAD}>cost</th>
            </tr>
          </thead>
          <tbody>
            {byModel.map((row) => (
              <tr key={row.model}>
                <td className="px-2 py-0.5">{row.model}</td>
                <td className={CELL}>{row.rounds}</td>
                <td className={CELL}>{formatCount(row.input)}</td>
                <td className={CELL}>{formatCount(row.cached)}</td>
                <td className={CELL}>{formatCount(row.output)}</td>
                <td className={CELL}>{formatCredits(row.credits)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      <Section title="By token kind">
        <TokenSplit debug={debug} config={config} />
      </Section>

      <Section title="Most expensive rounds">
        <ol className="m-0 flex list-none flex-col p-0">
          {top.map(({ agent, round }) => (
            <li key={round.row.id}>
              <button
                type="button"
                className="flex w-full items-center gap-2 px-2 py-0.5 text-left hover:bg-ds-slate-100"
                onClick={() => {
                  onSelect({ kind: "round", id: round.row.id });
                }}
              >
                <span className="min-w-0 flex-1 truncate">{`${agent.label}, round ${String(round.index)}`}</span>
                <span className="w-16 shrink-0 text-right tabular-nums">
                  {formatMs(round.end - round.start)}
                </span>
                <span className="w-20 shrink-0 text-right tabular-nums">
                  {formatCredits(round.credits)}
                </span>
              </button>
            </li>
          ))}
        </ol>
      </Section>
    </div>
  );
}
